import logging from 'utils/logging';
import _ from 'lodash';
import LobbyWelcomeMoments from './LobbyWelcome';
import PlayerJoinedMoments from './PlayerJoined';
import InstructionsMoments from './Instructions';
import InstructionsDone from './InstructionsDone';
import QuestionSelectedMoment from './QuestionSelected';
import DailyDoubleRevealMoment from './DailyDoubleReveal';
import DailyDoubleWagerReactionMoment from './DailyDoubleWagerReaction';
import ReadQuestionMoment from './ReadQuestion';
import JudgingCompleteMoment from './JudgingComplete';
import RoundCompleteMoment from './RoundComplete';
import RoundIntroMoment from './RoundIntro';
import AllSubmissionsReceivedMoment from './AllSubmissionsReceived';
import FinalIntroMoment from './FinalIntro';
import FinalCategoryRevealMoment from './FinalCategoryReveal';
import FinalAllWagersInMoment from './FinalAllWagersIn';
import FinalQuestionReadMoment from './FinalQuestionRead';
import FinalAnsweringTimeoutMoment from './FinalAnsweringTimeout';
import FinalPlayerRevealMoment from './FinalPlayerReveal';

const log = logging.getLogger('moments');
log.setLevel('info');

// Keep track of previous players for player join detection
// This is specific to the moments system, not tied to any specific moment
let prevPlayersCache = [];

/**
 * Collection of game moments that trigger host narration
 * Each moment contains:
 * - id: Unique identifier for the moment
 * - shouldTrigger: Function that determines when this moment should trigger
 * - generateNarrationParams: Function that returns narration key and context
 * 
 * Note: Each moment type may have its own internal state tracking,
 * while the orchestrator tracks general state like which narrations have been played.
 */
const moments = [
  // Transition from pregame to lobby (first welcome to players)
  LobbyWelcomeMoments.trigger,
  // Player joined during lobby
  PlayerJoinedMoments.trigger,
  // Instructions screen moment
  InstructionsMoments.trigger,
  // Player readiness for instructions
  InstructionsDone.trigger,

  // Round intro moment
  RoundIntroMoment.trigger,
  // Question selected moment
  QuestionSelectedMoment.trigger,
  // Daily Double reveal (fires at dailyDouble.picked)
  DailyDoubleRevealMoment.trigger,
  // Daily Double wager reaction (fires at question.wagering after wager submitted, onComplete advances to question.reading)
  DailyDoubleWagerReactionMoment.trigger,
  ReadQuestionMoment.trigger,
  // Judging complete moment
  JudgingCompleteMoment.trigger,
  // Round complete moment (wrap-up narration)
  RoundCompleteMoment.trigger,
  // Auto-advance from answering to judging when all submissions are in
  AllSubmissionsReceivedMoment.trigger,

  // Final round moments
  FinalIntroMoment.trigger,
  FinalCategoryRevealMoment.trigger,
  FinalAllWagersInMoment.trigger,
  FinalQuestionReadMoment.trigger,
  FinalAnsweringTimeoutMoment.trigger,
  FinalPlayerRevealMoment.trigger,
];

/**
 * Collection of playback moments that determine when to play narrations
 * Each moment contains:
 * - id: Unique identifier
 * - narrationKey: The key of the narration to play (optional for dynamic keys)
 * - shouldPlay: Function that determines when this narration should be played
 * - onComplete: Optional function to execute after narration completes
 */
const playbackMoments = [
  // Play the lobby welcome narration
  LobbyWelcomeMoments.playback,
  // Play player joined welcome narrations
  PlayerJoinedMoments.playback,
  // Play instructions narration
  InstructionsMoments.playback,
  // Play instructions done actions
  InstructionsDone.playback,
  // Round intro narration (playback handled by RoundIntroScreen)
  RoundIntroMoment.playback,
  // Question selected narration
  QuestionSelectedMoment.playback,
  // Daily Double reveal narration (during dailyDouble.picked)
  DailyDoubleRevealMoment.playback,
  // Daily Double wager reaction (during question.wagering, onComplete advances to question.reading)
  DailyDoubleWagerReactionMoment.playback,
  ReadQuestionMoment.playback,
  // Judging complete narration
  JudgingCompleteMoment.playback,

  // Final round playback moments
  FinalIntroMoment.playback,
  ...FinalCategoryRevealMoment.playback,
  ...FinalQuestionReadMoment.playback,
  ...FinalPlayerRevealMoment.playback,
  // Round complete narration (wrap-up)
  RoundCompleteMoment.playback,
].filter((m) => m && m.momentType);

/**
 * Get triggered moments for a state change
 * @param {Object} prevState - Previous game state
 * @param {Object} currentState - Current game state
 * @param {Array} players - Current players
 * @param {Array} questions - Current questions
 * @param {Array} narrationKeys - Existing narration keys
 * @param {Array} submissions - Player submissions
 * @param {Object} gameObject - Full game object with categories and questions
 * @returns {Array} - Array of triggered moments with narration params
 */
export function getTriggeredMoments(prevState, currentState, players, questions, narrationKeys = [], submissions = [], gameObject = null) {
  if (!currentState) return [];

  const triggered = [];

  // Make a copy of the current players to compare with later
  const currentPlayersCopy = players ? [...players] : [];

  // Get a reference to previous players for player join detection
  const prevPlayers = [...prevPlayersCache];

  // Update the cache for next time
  prevPlayersCache = currentPlayersCopy;

  moments.forEach(moment => {
    try {
      // Check if this moment should trigger, if so, it should return the narration params as an object
      const result = moment.shouldTrigger({ prevState, currentState, currentPlayers: players, prevPlayers, questions, submissions, gameObject });
      
      // If the moment triggered
      if (result) {
        log.info(`Moment triggered: ${moment.id} with params ${result}`);

        if (narrationKeys.includes(result.narrationKey)) {
          // This moment has already been triggered, so we skip it
          log.warn(`Moment triggered: ${moment.id} with key ${result} but it already exists`);
          return;
        }
        
        triggered.push({
          id: moment.id,
          ...result
        });
      }
    } catch (error) {
      log.error(`Error in moment ${moment.id}:`, error);
    }
  });
  
  return triggered;
}

/**
 * Get playback moments that should be played in the current state
 * @param {Object} gameState - Current game state
 * @param {Object} hostState - Current host state
 * @param {Set} playedNarrations - Set of already played narration keys (maintained by the orchestrator)
 * @returns {Array} - Array of playback moments that should be played
 */
export function getPlaybackMoments(gameState, hostState, narrations, playedNarrations = new Set(), players = []) {
  log.debug('getPlaybackMoments', gameState, hostState, narrations, playedNarrations);
  if (!gameState || !hostState) return [];
  
  const triggered = [];
  
  // Group narrations by moment type
  const narrationsByMomentType = {};
  _.forEach(narrations, narration => {
    if (!narration.momentType) return;
    
    if (!narrationsByMomentType[narration.momentType]) {
      narrationsByMomentType[narration.momentType] = [];
    }
    narrationsByMomentType[narration.momentType].push(narration);
  });
  
  // For each playback moment, check if it should trigger
  for (const moment of playbackMoments) {
    log.debug("Checking playback moment", moment);
    
    // Skip moments without a moment type
    if (!moment.momentType) {
      log.warn(`Skipping moment without momentType: ${moment.id}`);
      continue;
    }
    
    // Get all narrations of this moment type
    const momentNarrations = narrationsByMomentType[moment.momentType] || [];
    log.debug(`Found ${momentNarrations.length} narrations for moment type ${moment.momentType}`);
    
    if (momentNarrations.length === 0) {
      continue;
    }
    
    try {
      // Check each narration of this moment type
      for (const narration of momentNarrations) {
        // Check if this specific narration should play
        const shouldPlay = moment.shouldPlay(gameState, hostState, narration, playedNarrations, players);
        log.debug(`Should play narration ${narration.key || 'unknown'}? ${shouldPlay}`);
        
        // If it should play, add it to the triggered array
        if (shouldPlay) {
          log.info(`Playback moment triggered: ${moment.id} for narration: ${narration.key || 'unknown'}`);
          
          triggered.push({
            ...moment,
            narration,
            narrationId: narration.id,
            narrationKey: narration.key
          });
        }
      }
    } catch (error) {
      log.error(`Error in playback moment ${moment.id}:`, error);
    }
  }

  log.debug('Playback moments triggered:', triggered);
  
  return triggered;
}

const api = {
  moments,
  playbackMoments,
  getTriggeredMoments,
  getPlaybackMoments
}; 

export default api;